import React from "react";
import {
  DataLoadingWrapper,
  EmptyListContent,
  HeaderBreadcrumbs,
  MultiSectionLayout,
} from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import {
  WhatsappAccountProvider,
  InstagramAccountProvider,
  ClientSupportPageProvider,
} from "./providers";
import { useWhatsappAccount } from "./providers/WhatsappAccountProvider/useWhatsappAccount";
import { useInstagramAccount } from "./providers/InstagramAccountProvider/useInstagramAccount";
import { ConversationListSidebar } from "./components/ConversationListSidebar";
import { ClientSupportChat } from "./components/ClientSupportChat";
import { ReactComponent as PlatformsIcon } from "./assets/platforms.svg";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ManageAccountsDrawer } from "./components/ManageAccountsDrawer";
import { WebChatBotAccountProvider } from "./providers/WebChatBotAccountProvider";
import { useWebChatBotAccount } from "./providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { ManageAccountsDrawerProvider } from "./components/ManageAccountsDrawer/providers/ManageAccountsDrawerProvider";
import { useManageAccountsDrawer } from "./components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";

export const InnerClientSupportPage: React.FC = () => {
  const { t } = useTranslations();

  const {
    crmConfig,
    isManageAccountDrawerOpen,
    openManageAccountDrawer,
    isCRMConfigError,
    isCRMConfigLoading,
    refetchCRMConfig,
  } = useManageAccountsDrawer();

  const { whatsappAgent } = useWhatsappAccount();

  const { instagramAccount, isConnectingInstagramAccount } =
    useInstagramAccount();

  const { webChatBotAccount, isConnectingWebChatBotAccount } =
    useWebChatBotAccount();

  return (
    <>
      <DataLoadingWrapper
        isLoading={
          isConnectingInstagramAccount ||
          isConnectingWebChatBotAccount ||
          isCRMConfigLoading
        }
        isError={isCRMConfigError}
        onRetry={refetchCRMConfig}
      >
        {(() => {
          if (
            !whatsappAgent &&
            !instagramAccount &&
            !webChatBotAccount &&
            !crmConfig?.twilio?.id
          ) {
            return (
              <MultiSectionLayout
                header={
                  <HeaderBreadcrumbs
                    items={[
                      {
                        title: t({
                          id: "client-support-page.crm-settings",
                          defaultMessage: "CRM Settings",
                        }),
                      },
                    ]}
                  />
                }
              >
                <EmptyListContent
                  type="custom"
                  className="w-[460px]"
                  customImg={<PlatformsIcon />}
                  customTitle={t({
                    id: "client-support-page.welcome-to-crm-inbox",
                    defaultMessage: "Welcome to CRM Inbox",
                  })}
                  customSubtitle={t({
                    id: "client-support-page.connect-social-media-accounts",
                    defaultMessage:
                      "Connect your social media Accounts to receive and automate the messages from your Clients  🚀",
                  })}
                  customButton={{
                    text: t({
                      id: "client-support-page.add-new-account",
                      defaultMessage: "Add New Account",
                    }),
                    onClick: () =>
                      openManageAccountDrawer({ name: "connectedAccountList" }),
                    iconEnd: <AddIcon />,
                  }}
                />
              </MultiSectionLayout>
            );
          }

          return (
            <MultiSectionLayout leftSide={<ConversationListSidebar />}>
              <ClientSupportChat />
            </MultiSectionLayout>
          );
        })()}
      </DataLoadingWrapper>
      <ManageAccountsDrawer
        isOpen={!!isManageAccountDrawerOpen}
        onRequestClose={() => openManageAccountDrawer(undefined)}
        initialScreen={isManageAccountDrawerOpen}
      />
    </>
  );
};

export const ClientSupportPage: React.FC = () => {
  return (
    <ManageAccountsDrawerProvider isInboxModule>
      <WhatsappAccountProvider>
        <InstagramAccountProvider>
          <WebChatBotAccountProvider>
            <ClientSupportPageProvider>
              <InnerClientSupportPage />
            </ClientSupportPageProvider>
          </WebChatBotAccountProvider>
        </InstagramAccountProvider>
      </WhatsappAccountProvider>
    </ManageAccountsDrawerProvider>
  );
};
